import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { AttachmentWithImported, AttachmentDialogComponent } from 'guideline/feature-attachment';
import { produce } from 'immer';
import { ExpandService } from 'libs/shared/util-common/src/lib/expand.service';
import { take } from 'rxjs';
import { GuidelineService, GuidelineViewService, MergeConflictService } from 'shared/domain';
import {
  ButtonDirective,
  ChipComponent,
  ConfirmationDialogComponent,
  DialogService,
  IconDirective,
  PopoverService,
} from 'shared/ui-common';
import { compareHtmlText, isConnectedGuideline, isTruthy } from 'shared/util-common';

import { CommentableElementDirective } from '@treetop/shared/comments';
import { AttachmentsLinksReferencesTag } from '@treetop/shared/guideline-tags';
import {
  ComponentWidget,
  GuidelineActionSignificance,
  GuidelineComponentType,
  GuidelineTag,
  GuidelineTagStatus,
  ResourceTag,
  TextWidget,
  TextWidgetLocalChanges,
  UpdateStatus,
} from '@treetop/shared/model';
import { ResourceTagListComponent, AddResourceTagsComponent } from '@treetop/shared/tt-resource-tags';

import { GuidelineUpdateTagComponent } from '../../../../../../guideline-edit/edit-widgets/edit-chapter-widget/guideline-update-tag/guideline-update-tag.component';
import { CompareTextWidgetComponent } from '../../text/compare-text-widget.component';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-compare-component-widget',
  templateUrl: './compare-component-widget.component.html',
  styleUrls: ['./compare-component-widget.component.scss'],
  imports: [
    AttachmentsLinksReferencesTag,
    IconDirective,
    MatIconModule,
    TranslocoModule,
    GuidelineUpdateTagComponent,
    CommentableElementDirective,
    ButtonDirective,
    CompareTextWidgetComponent,
    ChipComponent,
    ResourceTagListComponent,
    AddResourceTagsComponent,
  ],
  providers: [MergeConflictService],
})
export class CompareComponentWidgetComponent {
  private expandService = inject(ExpandService);
  private guidelineService = inject(GuidelineService);
  protected guidelineViewService = inject(GuidelineViewService);
  private dialogService = inject(DialogService);
  private popoverService = inject(PopoverService);
  private viewContainerRef = inject(ViewContainerRef);

  private tagTriggerButton = viewChild<ElementRef>('tagTriggerButton');
  private addResourceTagsTemplate = viewChild<TemplateRef<unknown>>('addResourceTagsTemplate');

  public parentComponentId = input<string | null>(null);
  public widget = input.required<ComponentWidget>();
  protected component = computed(() => this.widget().component);
  protected descriptionWidget = computed(() => {
    return this.guidelineService.getWidget<TextWidget>(this.widget().widgetIds[0]);
  });
  protected allowEditTags = this.guidelineViewService?.allowEditTags ?? signal(false);

  protected guidelineData = this.guidelineService.guidelineData;

  protected childrenWidgets = computed(() => {
    return this.widget().childrenIds.map((childId) => this.guidelineService.getWidget<ComponentWidget>(childId));
  });

  protected hasLocalChange = computed(() => {
    const widget = this.widget();
    if (!widget) {
      return false;
    }

    return !!widget.localChanges?.attachments?.length;
  });

  protected hasDescriptionLocalChange = computed(() => {
    const widget = this.descriptionWidget();

    return !!widget.localChanges?.text;
  });

  protected selectedResourceTags = computed(() => {
    const importedResourceTags = this.widget()?.resourceTags ?? [];
    const localResourceTags = this.widget()?.localChanges?.resourceTags ?? [];
    if (!localResourceTags?.length) {
      return importedResourceTags;
    } else {
      return localResourceTags;
    }
  });

  protected hasContent = computed(() => !!this.widget().component.description || this.childrenWidgets().length > 0);
  protected expanded = computed(() => this.expandService.getExpandState(this.widget().uniqueId)());

  protected optional = computed(() => {
    const component = this.component();
    if (component.componentType === GuidelineComponentType.Action) {
      return component.significance === GuidelineActionSignificance.Optional;
    }
    return false;
  });

  protected medicationRequests = computed(() => {
    const component = this.component();
    if (component.componentType === GuidelineComponentType.Action) {
      return component.medicationRequests;
    }
    return [];
  });

  protected title = computed(() => {
    const component = this.component();
    if (component.title) {
      return component.title;
    } else if (component.componentType === GuidelineComponentType.Action) {
      return component.activityDefinition?.title;
    }
    return '';
  });

  protected attachments = computed(() => {
    const widget = this.widget();
    if (!widget) {
      return [];
    }

    const chapterAttachments = (widget.attachments ?? []).filter(
      (attachment) => widget.localChanges?.deletedOriginalAttachmentIds?.includes(attachment.id) !== true,
    );

    const localAttachments = this.widget()?.localChanges?.attachments ?? [];

    return [...chapterAttachments, ...localAttachments];
  });

  protected isEditMode = signal(false);
  protected isCompareMode = signal(false);
  protected hasMergeConflict = computed(() => {
    return this.mergeConflictStatus() === 'conflict';
  });

  protected originalTag = computed(() => {
    if (!this.widget()) {
      return undefined;
    }

    const originalTag = this.widget().tag;

    if (!originalTag) {
      return undefined;
    }

    if (isConnectedGuideline(this.guidelineData().guidelineOrigin)) {
      if (this.guidelineData().updateStatus === UpdateStatus.DEFAULT) {
        return undefined;
      }

      if (
        !this.guidelineViewService?.isSmartbookMode() &&
        !this.guidelineData().environment.hasPublishedVersion &&
        originalTag.status !== GuidelineTagStatus.NEW
      ) {
        return undefined;
      }
    }

    return originalTag;
  });

  protected localTag = computed(() => {
    const originalTag = this.widget().tag;
    const localTag = this.widget().localChanges?.tag;

    if (!localTag) {
      return undefined;
    }

    // If the original tag is deleted, we don't show local tag
    if (originalTag?.status === GuidelineTagStatus.DELETED) {
      return undefined;
    }

    if (!this.guidelineViewService?.isSmartbookMode() && !this.guidelineData().environment.hasPublishedVersion) {
      return undefined;
    }

    return localTag;
  });

  protected tag = computed(() => {
    if (!this.guidelineViewService?.showTags()) {
      return undefined;
    }

    return this.localTag() ?? this.originalTag();
  });

  private originalDescriptionLocalChanges = signal<TextWidgetLocalChanges>(undefined);

  constructor() {
    effect(() => {
      const widget = this.widget();

      if (!widget) {
        return;
      }

      this.expandService.addItem(widget.uniqueId, true, this.parentComponentId());
    });

    toObservable(this.descriptionWidget)
      .pipe(isTruthy(), take(1))
      .subscribe((widget) => {
        if (!widget) {
          return;
        }

        this.originalDescriptionLocalChanges.set(widget.localChanges);
      });
  }

  protected isMergeConflictResolved = signal(false);

  protected mergeConflictStatus = computed(() => {
    const widget = this.descriptionWidget();
    const localChanges = this.originalDescriptionLocalChanges();

    if (!localChanges) {
      return undefined;
    }

    if (!widget) {
      return undefined;
    }

    if (this.guidelineData()?.updateStatus !== UpdateStatus.UPDATED_WITH_CONFLICTS) {
      return undefined;
    }

    if (this.isMergeConflictResolved()) {
      return 'resolved';
    }

    if (widget.tag?.status !== GuidelineTagStatus.UPDATED && widget.tag?.status !== GuidelineTagStatus.CHILD_UPDATED) {
      return undefined;
    }

    if (!localChanges.text) {
      return undefined;
    }

    if (!compareHtmlText(widget.text, (localChanges as TextWidgetLocalChanges).text)) {
      return 'conflict';
    }

    return undefined;
  });

  protected toggleExpanded() {
    this.expandService.setExpandState(this.widget().uniqueId!, !this.expanded());
  }

  protected toggleEditMode($event: PointerEvent) {
    $event.stopPropagation();

    this.isEditMode.set(!this.isEditMode());

    if (this.isEditMode()) {
      this.expandService.setExpandState(this.widget().uniqueId!, true);
    }
  }

  protected toggleCompareMode($event: PointerEvent) {
    $event.stopPropagation();

    this.isCompareMode.set(!this.isCompareMode());

    if (this.isCompareMode()) {
      this.expandService.setExpandState(this.widget().uniqueId!, true);
    }
  }

  protected handleTagUpdated(tag: GuidelineTag) {
    if (isConnectedGuideline(this.guidelineData().guidelineOrigin)) {
      this.updateLocalTag(tag);
    } else {
      this.updateTag(tag);
    }
  }

  protected openAttachmentDialog($event: MouseEvent): void {
    $event.stopPropagation();
    const localAttachments: AttachmentWithImported[] = (this.widget()?.localChanges?.attachments || []).map((attachment) => ({
      ...attachment,
    }));

    const importedAttachments: AttachmentWithImported[] = (this.widget()?.attachments || []).map((attachment) => ({
      ...attachment,
      imported: true,
    }));

    const allAttachments = [...localAttachments, ...importedAttachments];
    const uniqueAttachments = Array.from(new Map(allAttachments.map((attachment) => [attachment.id, attachment])).values());

    const dialogRef = this.dialogService.open(AttachmentDialogComponent, {
      title: 'Assets',
      maxWidth: 1200,
      panelClass: 'attachment-dialog',
      disableClose: true,
      data: {
        attachedResources: uniqueAttachments ? uniqueAttachments : [],
        deletedImportedAttachmentIds: this.widget()?.localChanges?.deletedOriginalAttachmentIds ?? [],
      },
    });

    dialogRef.afterClosed().subscribe((result: { attachedResources: AttachmentWithImported[]; deletedImportedResourceIds: string[] }) => {
      if (result) {
        this.assignAttachments(result.attachedResources, result.deletedImportedResourceIds);
      }
    });
  }

  protected handleResourceTagLocalChanges(updatedTags: ResourceTag[]) {
    const updatedLocalChanges = produce(this.widget()?.localChanges ?? {}, (draft) => {
      draft.resourceTags = updatedTags;
    });

    this.guidelineService.updateWidgetLocal(this.widget()?.uniqueId, updatedLocalChanges);
  }

  protected openTagDropdown(event: MouseEvent): void {
    event.stopPropagation();

    const triggerEl = this.tagTriggerButton();
    const template = this.addResourceTagsTemplate();
    if (!triggerEl || !template) return;

    this.popoverService.open(template, triggerEl.nativeElement, { panelClass: 'tt-tag-dropdown-panel' }, this.viewContainerRef);
  }

  protected removeTag(tag: ResourceTag): void {
    this.handleResourceTagLocalChanges(this.selectedResourceTags().filter((t) => t.id !== tag.id));
  }

  private assignAttachments(attachments: AttachmentWithImported[], deletedImportedAttachmentsIds: string[] = []): void {
    const updatedLocalChanges = produce(this.widget()?.localChanges ?? {}, (draft) => {
      draft.attachments = attachments.filter((attachment) => !attachment.imported);
      draft.deletedOriginalAttachmentIds = [...(draft?.deletedOriginalAttachmentIds ?? []), ...deletedImportedAttachmentsIds];
    });

    this.guidelineService.updateWidgetLocal(this.widget()?.uniqueId, updatedLocalChanges);
  }

  private updateLocalTag(tag: GuidelineTag | undefined) {
    this.guidelineService.updateWidgetLocalTag(this.widget()?.uniqueId, tag);
  }

  private updateTag(tag: GuidelineTag | undefined) {
    const updatedChapter = produce(this.widget(), (draft) => {
      draft.tag = tag;
    });

    this.guidelineService.updateWidget(this.widget()?.uniqueId, updatedChapter);
  }

  public handleUnresolveMergeConflict() {
    this.dialogService
      .open(ConfirmationDialogComponent, {
        title: 'SingleGuidelineManager.ImportedGuideline.Editor.UnresolveMergeConflictDialog.Title',
        data: {
          message: 'SingleGuidelineManager.ImportedGuideline.Editor.UnresolveMergeConflictDialog.Message',
          cancelButtonCaption: 'Cancel',
          confirmButtonCaption: 'SingleGuidelineManager.ImportedGuideline.Editor.UnresolveMergeConflictDialog.Confirm',
        },
      })
      .afterClosed()
      .pipe(isTruthy())
      .subscribe(() => {
        this.isMergeConflictResolved.set(false);
        this.guidelineService.updateWidgetLocal(this.descriptionWidget()?.uniqueId, this.originalDescriptionLocalChanges());
      });
  }

  protected readonly GuidelineActionSignificance = GuidelineActionSignificance;
}
